import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
  Divider,
  Alert,
  Chip,
  Grid,
  Card,
  CardContent,
  CardActions,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Stepper,
  Step,
  StepLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Avatar,
  Stack,
  Container,
  Fade,
  Grow,
  CircularProgress
} from '@mui/material';
import {
  DragIndicator,
  Add,
  Delete,
  Edit,
  Save,
  Preview,
  ExpandMore,
  TextFields,
  CheckBox,
  RadioButtonChecked,
  Create,
  Email,
  Image,
  Visibility,
  GetApp,
  Settings,
  ViewHeadline,
  FormatQuote,
  Assignment,
  NavigateNext,
  NavigateBefore,
  CheckCircle,
  Info,
  PlayArrow,
  Stop,
  LocalHospital,
  Send,
  Gavel,
  Security,
  VerifiedUser,
  Fingerprint,
  Lock,
  VerifiedOutlined,
  ArrowUpward,
  ArrowDownward,
  DragHandle,
  // NEW: Artist section icons
  PersonOutline,
  Brush,
  Badge,
  RateReview,
  HorizontalRule
} from '@mui/icons-material';
import SignatureCanvas from './SignatureCanvas';
import { UseAuthRoles } from '../Hooks/UseAuthRoles';
import CombinedLegalCompliance from '../Legal/CombinedLegalCompliance';

// Component types for the form builder
const COMPONENT_TYPES = {
  TEXT_INPUT: 'text_input',
  CHECKBOX: 'checkbox',
  RADIO_GROUP: 'radio_group',
  YES_NO: 'yes_no',
  SIGNATURE: 'signature',
  INITIAL_BOX: 'initial_box',
  STATIC_TEXT: 'static_text',
  SECTION_HEADER: 'section_header',
  AFTERCARE_INSTRUCTIONS: 'aftercare_instructions',
  // NEW: Artist section components
  ARTIST_SECTION_DIVIDER: 'artist_section_divider',
  ARTIST_NUMBER: 'artist_number',
  ARTIST_REVIEW_CHECKBOX: 'artist_review_checkbox',
  ARTIST_SIGNATURE: 'artist_signature'
};

// Enhanced E-Signature Consent Component
const ESignatureConsentComponent = ({ element, formData, handleChange }) => {
  const [consentChecked, setConsentChecked] = useState(false);
  const [disclosureAcknowledged, setDisclosureAcknowledged] = useState(false);

  useEffect(() => {
    // Update parent form data when consent states change
    handleChange('esignature_consent', consentChecked);
    handleChange('disclosure_acknowledged', disclosureAcknowledged);
  }, [consentChecked, disclosureAcknowledged]);

  return (
    <Paper sx={{ p: 3, mb: 3, border: 2, borderColor: 'primary.main', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Security sx={{ color: 'primary.main', mr: 1 }} />
        <Typography variant="h6" color="primary" fontWeight={600}>
          Electronic Signature Consent & Disclosure
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Electronic Signature Disclosure:</strong> By checking the boxes below, you consent to use electronic signatures 
          and records for this document in compliance with the Electronic Signatures in Global and National Commerce Act (ESIGN).
        </Typography>
      </Alert>

      <Box sx={{ bgcolor: 'action.hover', p: 3, borderRadius: 1, mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
          <strong>Your Rights and Options:</strong>
        </Typography>
        <Typography variant="body2" component="div" sx={{ ml: 2, lineHeight: 1.6 }}>
          â€¢ You have the right to receive a paper copy of this document<br/>
          â€¢ You can withdraw your consent to electronic signatures at any time<br/>
          â€¢ Electronic signatures have the same legal effect as handwritten signatures under ESIGN Act<br/>
          â€¢ You confirm you have access to electronic devices to view and retain these documents<br/>
          â€¢ System requirements: Modern web browser with JavaScript enabled
        </Typography>
      </Box>

      <Stack spacing={2}>
        <FormControlLabel
          control={
            <Checkbox
              checked={disclosureAcknowledged}
              onChange={(e) => setDisclosureAcknowledged(e.target.checked)}
              required
            />
          }
          label={
            <Typography variant="body2">
              <strong>I acknowledge</strong> that I have read and understand the Electronic Signature Disclosure above
            </Typography>
          }
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={consentChecked}
              onChange={(e) => setConsentChecked(e.target.checked)}
              required
            />
          }
          label={
            <Typography variant="body2">
              <strong>I consent</strong> to the use of electronic signatures and electronic records for this document
            </Typography>
          }
        />
      </Stack>

      {consentChecked && disclosureAcknowledged && (
        <Alert severity="success" sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CheckCircle sx={{ mr: 1 }} />
            <Typography variant="body2">
              Electronic signature consent confirmed. You may now proceed to sign electronically.
            </Typography>
          </Box>
        </Alert>
      )}
    </Paper>
  );
};

// PKI Verification Component
const PKIVerificationComponent = ({ element }) => {
  const [verificationStatus, setVerificationStatus] = useState('pending');
  const [documentHash, setDocumentHash] = useState('');
  const [timestamp, setTimestamp] = useState('');

  useEffect(() => {
    // Simulate PKI verification process
    const generateVerificationData = () => {
      // Generate mock document hash (in real implementation, this would be calculated)
      const hash = 'SHA-256:' + Array.from({length: 8}, () => 
        Math.random().toString(16).substr(2, 4)).join('').toUpperCase();
      setDocumentHash(hash);
      
      // Set timestamp
      setTimestamp(new Date().toISOString());
      
      // Simulate verification completion
      setTimeout(() => {
        setVerificationStatus('verified');
      }, 2000);
    };

    generateVerificationData();
  }, []);

  return (
    <Paper sx={{ p: 3, mb: 3, border: '2px solid #4caf50', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <VerifiedUser sx={{ color: '#4caf50', mr: 1 }} />
        <Typography variant="h6" color="success.main" fontWeight={600}>
          PKI Security Verification
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          This document is protected by Public Key Infrastructure (PKI) technology ensuring authenticity, 
          integrity, and non-repudiation of your electronic signature.
        </Typography>
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Fingerprint sx={{ color: 'primary.main', mr: 1, fontSize: 20 }} />
              <Typography variant="subtitle2">Document Integrity</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {documentHash || 'Calculating...'}
            </Typography>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <VerifiedOutlined sx={{ color: 'primary.main', mr: 1, fontSize: 20 }} />
              <Typography variant="subtitle2">Certificate Authority</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {timestamp ? new Date(timestamp).toLocaleString() : 'Generating...'}
            </Typography>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Lock sx={{ 
                color: verificationStatus === 'verified' ? '#4caf50' : '#ff9800', 
                mr: 1, 
                fontSize: 20 
              }} />
              <Typography variant="subtitle2">
                Verification Status: {verificationStatus === 'verified' ? 'Secured' : 'Processing...'}
              </Typography>
            </Box>
            {verificationStatus === 'verified' ? (
              <Alert severity="success" sx={{ mt: 1 }}>
                <Typography variant="caption">
                  âœ“ Document cryptographically secured with 256-bit encryption
                  <br />âœ“ Tamper-evident digital seal applied
                  <br />âœ“ RFC 3161 compliant timestamp verification
                </Typography>
              </Alert>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                <Typography variant="caption">Applying cryptographic protections...</Typography>
              </Box>
            )}
          </Card>
        </Grid>
      </Grid>
    </Paper>
  );
};

// Enhanced Simple Form Element Component for editing with drag and move controls
const SimpleFormElement = ({ element, index, onUpdate, onDelete, onMove, totalElements, onReorderStart, onReorderEnd }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(element);
  const [isDragging, setIsDragging] = useState(false);

  const handleSave = () => {
    onUpdate(index, editData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditData(element);
    setIsEditing(false);
  };

  const handleMoveUp = () => {
    if (index > 0) {
      onMove(index, index - 1);
    }
  };

  const handleMoveDown = () => {
    if (index < totalElements - 1) {
      onMove(index, index + 1);
    }
  };

  // Drag handlers for reordering existing elements
  const handleDragStart = (e) => {
    // Set the drag data
    e.dataTransfer.setData('text/plain', index.toString()); // Fallback
    e.dataTransfer.setData('elementIndex', index.toString());
    e.dataTransfer.setData('dragType', 'reorder');
    e.dataTransfer.effectAllowed = 'move';
    
    // Create a drag image (optional, helps with visual feedback)
    if (e.target) {
      e.dataTransfer.setDragImage(e.target, 20, 20);
    }
    
    // Set local and parent states
    setIsDragging(true);
    if (onReorderStart) {
      onReorderStart();
    }
  };

  const handleDragEnd = (e) => {
    setIsDragging(false);
    if (onReorderEnd) {
      onReorderEnd();
    }
  };

  // Check if this is an artist section component
  const isArtistComponent = [
    COMPONENT_TYPES.ARTIST_SECTION_DIVIDER,
    COMPONENT_TYPES.ARTIST_NUMBER,
    COMPONENT_TYPES.ARTIST_REVIEW_CHECKBOX,
    COMPONENT_TYPES.ARTIST_SIGNATURE
  ].includes(element.type);

  const renderEditInterface = () => {
    switch (element.type) {
      case COMPONENT_TYPES.STATIC_TEXT:
        return (
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Text Content"
            value={editData.content || ''}
            onChange={(e) => setEditData({ ...editData, content: e.target.value })}
          />
        );

      case COMPONENT_TYPES.SECTION_HEADER:
        return (
          <TextField
            fullWidth
            label="Section Title"
            value={editData.content || ''}
            onChange={(e) => setEditData({ ...editData, content: e.target.value })}
          />
        );

      case COMPONENT_TYPES.TEXT_INPUT:
        return (
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Field Label"
              value={editData.label || ''}
              onChange={(e) => setEditData({ ...editData, label: e.target.value })}
            />
            <TextField
              fullWidth
              label="Placeholder Text"
              value={editData.placeholder || ''}
              onChange={(e) => setEditData({ ...editData, placeholder: e.target.value })}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editData.required || false}
                  onChange={(e) => setEditData({ ...editData, required: e.target.checked })}
                />
              }
              label="Required Field"
            />
          </Stack>
        );

      case COMPONENT_TYPES.CHECKBOX:
        return (
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Checkbox Label"
              value={editData.label || ''}
              onChange={(e) => setEditData({ ...editData, label: e.target.value })}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editData.required || false}
                  onChange={(e) => setEditData({ ...editData, required: e.target.checked })}
                />
              }
              label="Required Field"
            />
          </Stack>
        );

      case COMPONENT_TYPES.YES_NO:
        return (
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Question"
              value={editData.question || ''}
              onChange={(e) => setEditData({ ...editData, question: e.target.value })}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editData.required || false}
                  onChange={(e) => setEditData({ ...editData, required: e.target.checked })}
                />
              }
              label="Required Field"
            />
          </Stack>
        );

      case COMPONENT_TYPES.RADIO_GROUP:
        return (
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Question"
              value={editData.question || ''}
              onChange={(e) => setEditData({ ...editData, question: e.target.value })}
            />
            <TextField
              fullWidth
              label="Options (one per line)"
              multiline
              rows={6}
              value={(editData.options || []).join('\n')}
              onChange={(e) => {
                const lines = e.target.value.split('\n');
                setEditData({
                  ...editData,
                  options: lines
                });
              }}
              onBlur={(e) => {
                const lines = e.target.value.split('\n');
                const cleanedOptions = lines
                  .map(line => line.trim())
                  .filter((line, index, array) => {
                    if (line) return true;
                    return index < array.length - 1 && array.slice(index + 1).some(l => l.trim());
                  });
                
                if (cleanedOptions.length === 0) {
                  cleanedOptions.push('Option 1');
                }
                
                setEditData({
                  ...editData,
                  options: cleanedOptions
                });
              }}
              placeholder="Enter each option on a new line&#10;Option 1&#10;Option 2&#10;Option 3"
              helperText="Press Enter to create a new line. Empty lines will be removed when you finish editing."
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editData.required || false}
                  onChange={(e) => setEditData({ ...editData, required: e.target.checked })}
                />
              }
              label="Required Field"
            />
          </Stack>
        );

      case COMPONENT_TYPES.AFTERCARE_INSTRUCTIONS:
        return (
          <Stack spacing={2}>
            <TextField
              fullWidth
              multiline
              rows={6}
              label="Aftercare Instructions"
              value={editData.instructions || ''}
              onChange={(e) => setEditData({ ...editData, instructions: e.target.value })}
              placeholder="Enter your custom aftercare instructions here. This will be included in the email sent to clients after form submission..."
              helperText="These instructions will be automatically sent to clients via email when aftercare is enabled."
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editData.enabled !== false}
                  onChange={(e) => setEditData({ ...editData, enabled: e.target.checked })}
                />
              }
              label="Enable aftercare email for this form"
            />
          </Stack>
        );

      // NEW: Artist Section Divider editor
      case COMPONENT_TYPES.ARTIST_SECTION_DIVIDER:
        return (
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Divider Title"
              value={editData.title || 'Artist Section'}
              onChange={(e) => setEditData({ ...editData, title: e.target.value })}
              helperText="This text will appear as a header above the artist section"
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Artist Section Instructions (optional)"
              value={editData.instructions || ''}
              onChange={(e) => setEditData({ ...editData, instructions: e.target.value })}
              placeholder="Optional instructions for the artist..."
              helperText="Brief instructions that will appear below the divider title"
            />
          </Stack>
        );

      // NEW: Artist Number editor
      case COMPONENT_TYPES.ARTIST_NUMBER:
        return (
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Field Label"
              value={editData.label || 'Artist/License Number'}
              onChange={(e) => setEditData({ ...editData, label: e.target.value })}
            />
            <TextField
              fullWidth
              label="Placeholder"
              value={editData.placeholder || 'Enter your certification/license number'}
              onChange={(e) => setEditData({ ...editData, placeholder: e.target.value })}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editData.required !== false}
                  onChange={(e) => setEditData({ ...editData, required: e.target.checked })}
                />
              }
              label="Required Field"
            />
          </Stack>
        );

      // NEW: Artist Review Checkbox editor
      case COMPONENT_TYPES.ARTIST_REVIEW_CHECKBOX:
        return (
          <Stack spacing={2}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Review Confirmation Text"
              value={editData.label || 'I have reviewed this form and confirm all information is accurate'}
              onChange={(e) => setEditData({ ...editData, label: e.target.value })}
              helperText="This checkbox confirms the artist has reviewed the client's form"
            />
          </Stack>
        );

      // NEW: Artist Signature - no editing needed
      case COMPONENT_TYPES.ARTIST_SIGNATURE:
        return (
          <Alert severity="info">
            The artist signature canvas will be displayed in this location. No configuration needed.
          </Alert>
        );

      default:
        return (
          <Typography variant="body2" color="text.secondary">
            No editing options available for this component type.
          </Typography>
        );
    }
  };

  const renderPreview = () => {
    switch (element.type) {
      case COMPONENT_TYPES.STATIC_TEXT:
        return (
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {element.content || 'Empty text block'}
          </Typography>
        );

      case COMPONENT_TYPES.SECTION_HEADER:
        return (
          <Typography variant="h6" fontWeight={600} color="primary">
            {element.content || 'Section Header'}
          </Typography>
        );

      case COMPONENT_TYPES.TEXT_INPUT:
        return (
          <TextField
            fullWidth
            label={element.label || 'Text Input'}
            placeholder={element.placeholder}
            disabled
            size="small"
          />
        );

      case COMPONENT_TYPES.CHECKBOX:
        return (
          <FormControlLabel
            control={<Checkbox disabled />}
            label={element.label || 'Checkbox'}
          />
        );

      case COMPONENT_TYPES.YES_NO:
        return (
          <Box>
            <Typography variant="body1" gutterBottom>
              {element.question || 'Yes/No Question'}
            </Typography>
            <RadioGroup row>
              <FormControlLabel control={<Radio disabled />} label="Yes" />
              <FormControlLabel control={<Radio disabled />} label="No" />
            </RadioGroup>
          </Box>
        );

      case COMPONENT_TYPES.RADIO_GROUP:
        return (
          <Box>
            <Typography variant="body1" gutterBottom>
              {element.question || 'Multiple Choice Question'}
            </Typography>
            <RadioGroup>
              {(element.options || ['Option 1', 'Option 2']).map((option, idx) => (
                <FormControlLabel
                  key={idx}
                  control={<Radio disabled />}
                  label={option}
                />
              ))}
            </RadioGroup>
          </Box>
        );

      case COMPONENT_TYPES.AFTERCARE_INSTRUCTIONS:
        return (
          <Alert severity="info" icon={<LocalHospital />}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Aftercare Instructions Component</strong>
            </Typography>
            {element.instructions ? (
              <Typography variant="body2" sx={{
                whiteSpace: 'pre-wrap',
                fontStyle: 'italic',
                maxHeight: '100px',
                overflow: 'auto',
                bgcolor: 'rgba(255,255,255,0.5)',
                p: 1,
                borderRadius: 1,
                border: '1px solid rgba(0,0,0,0.1)'
              }}>
                {element.instructions.length > 200
                  ? `${element.instructions.substring(0, 200)}...`
                  : element.instructions
                }
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No custom instructions set - will use default aftercare message
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Status: {element.enabled !== false ? 'Enabled' : 'Disabled'}
            </Typography>
          </Alert>
        );

      // NEW: Artist Section Divider preview
      case COMPONENT_TYPES.ARTIST_SECTION_DIVIDER:
        return (
          <Box sx={{ 
            p: 2, 
            bgcolor: 'action.hover', 
            borderRadius: 1, 
            border: '2px dashed', borderColor: 'primary.main',
            textAlign: 'center'
          }}>
            <HorizontalRule sx={{ color: 'primary.main', mb: 1 }} />
            <Typography variant="h6" color="primary" fontWeight={600}>
              {element.title || 'Artist Section'}
            </Typography>
            {element.instructions && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {element.instructions}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              â¬†ï¸ Client Section Above | â¬‡ï¸ Artist Section Below
            </Typography>
          </Box>
        );

      // NEW: Artist Number preview
      case COMPONENT_TYPES.ARTIST_NUMBER:
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Badge color="secondary" />
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {element.label || 'Artist/License Number'} {element.required !== false && '*'}
              </Typography>
              <TextField
                size="small"
                placeholder={element.placeholder || 'Enter certification number'}
                disabled
                sx={{ width: '250px' }}
              />
            </Box>
          </Box>
        );

      // NEW: Artist Review Checkbox preview
      case COMPONENT_TYPES.ARTIST_REVIEW_CHECKBOX:
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RateReview color="secondary" />
            <FormControlLabel
              control={<Checkbox disabled />}
              label={element.label || 'I have reviewed this form and confirm all information is accurate'}
            />
          </Box>
        );

      // NEW: Artist Signature preview
      case COMPONENT_TYPES.ARTIST_SIGNATURE:
        return (
          <Box sx={{ 
            p: 2, 
            bgcolor: 'action.hover', 
            borderRadius: 1, 
            border: 1, borderColor: 'secondary.light',
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            <Brush sx={{ color: 'secondary.main' }} />
            <Box>
              <Typography variant="body2" fontWeight={600} color="secondary">
                Artist Signature Canvas
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Artist will sign here after reviewing the form
              </Typography>
            </Box>
          </Box>
        );

      default:
        return (
          <Typography variant="body2" color="text.secondary">
            {element.type} component
          </Typography>
        );
    }
  };

  return (
    <Paper
      elevation={isDragging ? 4 : 2}
      draggable={!isEditing}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      sx={{
        p: 2,
        mb: 2,
        border: isEditing ? 2 : isDragging ? '2px dashed' : isArtistComponent ? 2 : 1,
        borderColor: isEditing ? 'primary.main' : isDragging ? 'primary.main' : isArtistComponent ? 'secondary.main' : 'divider',
        position: 'relative',
        opacity: isDragging ? 0.5 : 1,
        cursor: isEditing ? 'default' : 'grab',
        bgcolor: isArtistComponent ? 'action.hover' : 'background.paper',
        '&:hover': {
          boxShadow: 3
        },
        '&:active': {
          cursor: isEditing ? 'default' : 'grabbing'
        }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Drag handle indicator */}
          <Tooltip title="Drag to reorder">
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              mr: 1,
              cursor: 'grab',
              color: 'grey.400',
              '&:hover': { color: 'grey.600' }
            }}>
              <DragHandle />
            </Box>
          </Tooltip>
          {/* Move controls */}
          <Box sx={{ display: 'flex', flexDirection: 'column', mr: 1 }}>
            <Tooltip title="Move up">
              <span>
                <IconButton 
                  size="small" 
                  onClick={handleMoveUp}
                  disabled={index === 0}
                  sx={{ 
                    p: 0.5,
                    color: index === 0 ? 'grey.300' : 'grey.600',
                    '&:hover': { color: 'primary.main' }
                  }}
                >
                  <ArrowUpward fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Move down">
              <span>
                <IconButton 
                  size="small" 
                  onClick={handleMoveDown}
                  disabled={index === totalElements - 1}
                  sx={{ 
                    p: 0.5,
                    color: index === totalElements - 1 ? 'grey.300' : 'grey.600',
                    '&:hover': { color: 'primary.main' }
                  }}
                >
                  <ArrowDownward fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Chip
            label={element.type.replace(/_/g, ' ').toUpperCase()}
            size="small"
            color={isArtistComponent ? 'secondary' : 'primary'}
            variant="outlined"
          />
          {isArtistComponent && (
            <Chip size="small" label="Artist Section" color="secondary" sx={{ fontSize: '10px' }} />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton size="small" onClick={() => setIsEditing(!isEditing)}>
            <Edit />
          </IconButton>
          <IconButton size="small" onClick={() => onDelete(index)} color="error">
            <Delete />
          </IconButton>
        </Box>
      </Box>

      {isEditing ? (
        <Box>
          {renderEditInterface()}
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button size="small" variant="contained" onClick={handleSave}>
              Save
            </Button>
            <Button size="small" variant="outlined" onClick={handleCancel}>
              Cancel
            </Button>
          </Box>
        </Box>
      ) : (
        renderPreview()
      )}
    </Paper>
  );
};

// Enhanced Component Library for dragging/adding components
const ComponentLibrary = ({ onAddComponent, onDragStart, onDragEnd }) => {
  const { hasAftercareEmailAccess } = UseAuthRoles();

  const clientComponents = [
    {
      type: COMPONENT_TYPES.SECTION_HEADER,
      label: 'Section Title',
      icon: <ViewHeadline />,
      description: 'Add section headers to organize your form'
    },
    {
      type: COMPONENT_TYPES.STATIC_TEXT,
      label: 'Text Block',
      icon: <FormatQuote />,
      description: 'Add instructions, legal text, or information'
    },
    {
      type: COMPONENT_TYPES.TEXT_INPUT,
      label: 'Text Input',
      icon: <TextFields />,
      description: 'Single-line text input field'
    },
    {
      type: COMPONENT_TYPES.CHECKBOX,
      label: 'Checkbox',
      icon: <CheckBox />,
      description: 'Single checkbox for agreements'
    },
    {
      type: COMPONENT_TYPES.YES_NO,
      label: 'Yes/No Question',
      icon: <RadioButtonChecked />,
      description: 'Binary choice question'
    },
    {
      type: COMPONENT_TYPES.RADIO_GROUP,
      label: 'Multiple Choice',
      icon: <RadioButtonChecked />,
      description: 'Multiple choice question with options'
    },
    // Only show aftercare instructions if user has access
    ...(hasAftercareEmailAccess() ? [{
      type: COMPONENT_TYPES.AFTERCARE_INSTRUCTIONS,
      label: 'Aftercare Instructions',
      icon: <LocalHospital />,
      description: 'Automated aftercare email integration'
    }] : [])
  ];

  // NEW: Artist section components
  const artistComponents = [
    {
      type: COMPONENT_TYPES.ARTIST_SECTION_DIVIDER,
      label: 'Artist Section Divider',
      icon: <HorizontalRule />,
      description: 'Visual break between client and artist sections',
      highlight: true
    },
    {
      type: COMPONENT_TYPES.ARTIST_NUMBER,
      label: 'Artist Number Field',
      icon: <Badge />,
      description: 'Field for artist certification/license number'
    },
    {
      type: COMPONENT_TYPES.ARTIST_REVIEW_CHECKBOX,
      label: 'Artist Review Checkbox',
      icon: <RateReview />,
      description: 'Checkbox for artist to confirm form review'
    },
    {
      type: COMPONENT_TYPES.ARTIST_SIGNATURE,
      label: 'Artist Signature',
      icon: <Brush />,
      description: 'Signature canvas for the artist'
    }
  ];

  const handleDragStartInternal = (e, componentType) => {
    e.dataTransfer.setData('componentType', componentType);
    e.dataTransfer.setData('dragType', 'new');
    e.dataTransfer.effectAllowed = 'copy';
    if (onDragStart) onDragStart(componentType);
  };

  const handleDragEndInternal = () => {
    if (onDragEnd) onDragEnd();
  };

  const renderComponentCard = (component, isArtist = false) => (
    <Card
      key={component.type}
      variant="outlined"
      draggable
      onDragStart={(e) => handleDragStartInternal(e, component.type)}
      onDragEnd={handleDragEndInternal}
      sx={{
        cursor: 'grab',
        transition: 'all 0.2s',
        borderColor: isArtist ? 'secondary.light' : 'divider',
        bgcolor: isArtist ? 'action.hover' : 'background.paper',
        '&:hover': {
          backgroundColor: isArtist ? 'action.hover' : 'action.hover',
          boxShadow: 2,
          borderColor: isArtist ? 'secondary.main' : 'primary.main'
        },
        '&:active': {
          cursor: 'grabbing'
        }
      }}
      onClick={() => onAddComponent(component.type)}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <DragHandle sx={{ color: 'grey.400', mr: 0.5, fontSize: 18 }} />
          <Box sx={{ color: isArtist ? 'secondary.main' : 'text.primary' }}>
            {component.icon}
          </Box>
          <Typography variant="subtitle2" sx={{ ml: 1, fontWeight: 600 }}>
            {component.label}
          </Typography>
          {component.highlight && (
            <Chip size="small" label="NEW" color="secondary" sx={{ ml: 1, fontSize: '9px', height: '18px' }} />
          )}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {component.description}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Paper elevation={2} sx={{ p: 2, height: 'fit-content', position: 'sticky', top: 20 }}>
      <Typography variant="h6" gutterBottom>
        Form Components
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Drag or click to add components
      </Typography>

      <Alert severity="info" sx={{ mb: 2, fontSize: '12px' }}>
        <Typography variant="body2" sx={{ fontSize: '12px' }}>
          <strong>Note:</strong> Terms & Conditions, E-Signature Consent 
          are automatically included at the bottom of the client section.
        </Typography>
      </Alert>

      {/* Client Section Components */}
      <Typography variant="subtitle2" sx={{ mb: 1, mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <PersonOutline fontSize="small" /> Client Section
      </Typography>
      <Stack spacing={1} sx={{ mb: 3 }}>
        {clientComponents.map((component) => renderComponentCard(component, false))}
      </Stack>

      <Divider sx={{ my: 2 }} />

      {/* Artist Section Components */}
      <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1, color: 'secondary.main' }}>
        <Brush fontSize="small" /> Artist Section
      </Typography>
      <Alert severity="warning" sx={{ mb: 2, fontSize: '11px' }}>
        <Typography variant="body2" sx={{ fontSize: '11px' }}>
          Add the <strong>Artist Section Divider</strong> first to create a visual break. 
          Content after the divider is for artist completion only.
        </Typography>
      </Alert>
      <Stack spacing={1}>
        {artistComponents.map((component) => renderComponentCard(component, true))}
      </Stack>
    </Paper>
  );
};

// Enhanced Professional Form Renderer Component with automatic legal compliance
const ProfessionalFormRenderer = ({ formMetadata, formElements }) => {
  const [formData, setFormData] = useState({});
  const [selectedClient, setSelectedClient] = useState(null);
  const [sendAftercareEmail, setSendAftercareEmail] = useState(false);
  const [signatureData, setSignatureData] = useState('');
  const [artistSignatureData, setArtistSignatureData] = useState('');
  const [artistNumber, setArtistNumber] = useState('');
  const [artistReviewConfirmed, setArtistReviewConfirmed] = useState(false);
  const [legalCompliance, setLegalCompliance] = useState({
    allComplianceComplete: false
  });

  const mockClients = [
    { id: 1, first_name: 'John', last_name: 'Doe', email: 'john.doe@example.com' },
    { id: 2, first_name: 'Jane', last_name: 'Smith', email: 'jane.smith@example.com' }
  ];

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLegalComplianceChange = (compliance) => {
    setLegalCompliance(compliance);
  };

  // Find the artist section divider index
  const artistDividerIndex = formElements.findIndex(el => el.type === COMPONENT_TYPES.ARTIST_SECTION_DIVIDER);
  const hasArtistSection = artistDividerIndex !== -1;
  
  // Split elements into client and artist sections
  const clientElements = hasArtistSection 
    ? formElements.slice(0, artistDividerIndex) 
    : formElements.filter(el => ![
        COMPONENT_TYPES.ARTIST_SECTION_DIVIDER,
        COMPONENT_TYPES.ARTIST_NUMBER,
        COMPONENT_TYPES.ARTIST_REVIEW_CHECKBOX,
        COMPONENT_TYPES.ARTIST_SIGNATURE
      ].includes(el.type));
      
  const artistElements = hasArtistSection 
    ? formElements.slice(artistDividerIndex) 
    : formElements.filter(el => [
        COMPONENT_TYPES.ARTIST_SECTION_DIVIDER,
        COMPONENT_TYPES.ARTIST_NUMBER,
        COMPONENT_TYPES.ARTIST_REVIEW_CHECKBOX,
        COMPONENT_TYPES.ARTIST_SIGNATURE
      ].includes(el.type));

  const renderFormElement = (element, index) => {
    const name = element.name || `field_${index}`;

    switch (element.type) {
      case COMPONENT_TYPES.STATIC_TEXT:
        return (
          <Box
            key={index}
            sx={{
              p: 3,
              mb: 3,
              bgcolor: 'action.hover',
              border: 1, borderColor: 'divider',
              borderRadius: 1,
              fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif'
            }}
          >
            <Typography
              variant="body1"
              sx={{
                whiteSpace: 'pre-wrap',
                lineHeight: 1.6,
                fontSize: '14px',
                color: 'text.primary'
              }}
            >
              {element.content}
            </Typography>
          </Box>
        );

      case COMPONENT_TYPES.SECTION_HEADER:
        return (
          <Box key={index} sx={{ mb: 3, mt: index > 0 ? 4 : 0 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                borderBottom: 3, borderColor: 'primary.main',
                pb: 1,
                mb: 2
              }}
            >
              {element.content}
            </Typography>
          </Box>
        );

      case COMPONENT_TYPES.TEXT_INPUT:
        return (
          <Box key={index} sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label={element.label}
              placeholder={element.placeholder}
              required={element.required}
              value={formData[name] || ''}
              onChange={(e) => handleChange(name, e.target.value)}
              variant="outlined"
            />
          </Box>
        );

      case COMPONENT_TYPES.CHECKBOX:
        return (
          <Box key={index} sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData[name] || false}
                  onChange={(e) => handleChange(name, e.target.checked)}
                  required={element.required}
                />
              }
              label={element.label}
            />
          </Box>
        );

      case COMPONENT_TYPES.YES_NO:
        return (
          <Box key={index} sx={{ mb: 3 }}>
            <Typography variant="body1" gutterBottom sx={{ fontWeight: 500 }}>
              {element.question}
              {element.required && <span style={{ color: '#d32f2f' }}> *</span>}
            </Typography>
            <RadioGroup
              row
              value={formData[name] || ''}
              onChange={(e) => handleChange(name, e.target.value)}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
            </RadioGroup>
          </Box>
        );

      case COMPONENT_TYPES.RADIO_GROUP:
        return (
          <Box key={index} sx={{ mb: 3 }}>
            <Typography variant="body1" gutterBottom sx={{ fontWeight: 500 }}>
              {element.question}
              {element.required && <span style={{ color: '#d32f2f' }}> *</span>}
            </Typography>
            <RadioGroup
              value={formData[name] || ''}
              onChange={(e) => handleChange(name, e.target.value)}
            >
              {(element.options || []).map((option, idx) => (
                <FormControlLabel
                  key={idx}
                  value={option}
                  control={<Radio />}
                  label={option}
                />
              ))}
            </RadioGroup>
          </Box>
        );

      case COMPONENT_TYPES.AFTERCARE_INSTRUCTIONS:
        return (
          <Box key={index} sx={{ mb: 3 }}>
            {element.enabled !== false && (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Aftercare instructions will be automatically sent to your email after form submission.
                  </Typography>
                </Alert>
                {element.instructions && (
                  <Paper sx={{ p: 3, bgcolor: 'info.50', mb: 2 }}>
                    <Typography variant="h6" gutterBottom color="info.dark">
                      Aftercare Instructions Preview
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                      {element.instructions}
                    </Typography>
                  </Paper>
                )}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={sendAftercareEmail}
                      onChange={(e) => setSendAftercareEmail(e.target.checked)}
                    />
                  }
                  label="Send me aftercare instructions via email"
                />
              </>
            )}
          </Box>
        );

      // NEW: Artist Section Divider
      case COMPONENT_TYPES.ARTIST_SECTION_DIVIDER:
        return (
          <Box 
            key={index} 
            sx={{ 
              my: 4, 
              py: 3, 
              px: 4, 
              bgcolor: 'action.hover', 
              borderRadius: 2,
              border: '2px dashed', borderColor: 'primary.main',
              textAlign: 'center'
            }}
          >
            <Divider sx={{ mb: 2 }}>
              <Chip 
                icon={<Brush />} 
                label={element.title || 'Artist Section'} 
                color="primary"
                sx={{ fontSize: '14px', fontWeight: 600, py: 2.5 }}
              />
            </Divider>
            {element.instructions && (
              <Typography variant="body2" color="text.secondary">
                {element.instructions}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              The section below is to be completed by the artist
            </Typography>
          </Box>
        );

      // NEW: Artist Number
      case COMPONENT_TYPES.ARTIST_NUMBER:
        return (
          <Box key={index} sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label={element.label || 'Artist/License Number'}
              placeholder={element.placeholder || 'Enter your certification/license number'}
              required={element.required !== false}
              value={artistNumber}
              onChange={(e) => setArtistNumber(e.target.value)}
              variant="outlined"
              InputProps={{
                startAdornment: <Badge sx={{ mr: 1, color: 'secondary.main' }} />
              }}
            />
          </Box>
        );

      // NEW: Artist Review Checkbox
      case COMPONENT_TYPES.ARTIST_REVIEW_CHECKBOX:
        return (
          <Box key={index} sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1, border: 1, borderColor: 'secondary.light' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={artistReviewConfirmed}
                  onChange={(e) => setArtistReviewConfirmed(e.target.checked)}
                  color="secondary"
                />
              }
              label={
                <Typography variant="body2" fontWeight={500}>
                  {element.label || 'I have reviewed this form and confirm all information is accurate'}
                  <span style={{ color: 'red' }}> *</span>
                </Typography>
              }
            />
          </Box>
        );

      // NEW: Artist Signature
      case COMPONENT_TYPES.ARTIST_SIGNATURE:
        return (
          <Box key={index} sx={{ mb: 4 }}>
            <Typography variant="subtitle1" fontWeight={600} color="secondary" gutterBottom>
              Artist Signature
            </Typography>
            <SignatureCanvas
              onSignatureChange={(signature, type) => setArtistSignatureData(signature)}
              value={artistSignatureData}
              disabled={!artistReviewConfirmed}
              required={true}
              error={false}
              helperText={
                artistReviewConfirmed
                  ? "Sign to confirm your review"
                  : "Please confirm you have reviewed the form above before signing"
              }
            />
          </Box>
        );

      default:
        return null;
    }
  };

  // Check if artist section is complete
  const hasArtistSignatureElement = formElements.some(el => el.type === COMPONENT_TYPES.ARTIST_SIGNATURE);
  const hasArtistNumberElement = formElements.some(el => el.type === COMPONENT_TYPES.ARTIST_NUMBER);
  const hasArtistReviewElement = formElements.some(el => el.type === COMPONENT_TYPES.ARTIST_REVIEW_CHECKBOX);
  
  const artistSectionComplete = (
    (!hasArtistSignatureElement || artistSignatureData) &&
    (!hasArtistNumberElement || artistNumber) &&
    (!hasArtistReviewElement || artistReviewConfirmed)
  );

  const canSubmit = legalCompliance.allComplianceComplete && signatureData && 
    (!hasArtistSection || artistSectionComplete);

  return (
    <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4, pb: 3, borderBottom: 2, borderColor: 'divider' }}>
          <Typography variant="h4" fontWeight={700} color="primary" gutterBottom>
            {formMetadata.title}
          </Typography>
          {formMetadata.description && (
            <Typography variant="body1" color="text.secondary">
              {formMetadata.description}
            </Typography>
          )}
        </Box>

        {/* Client Selection */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonOutline /> Client Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Select Client"
                value={selectedClient?.id || ''}
                onChange={(e) => {
                  const client = mockClients.find(c => c.id === parseInt(e.target.value));
                  setSelectedClient(client);
                }}
                SelectProps={{ native: true }}
              >
                <option value="">Choose a client...</option>
                {mockClients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.first_name} {client.last_name} ({client.email})
                  </option>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Box>

        {/* CLIENT SECTION: Form Elements */}
        <Box sx={{ mb: 4 }}>
          {clientElements.map((element, index) => renderFormElement(element, index))}
        </Box>

        {/* AUTOMATIC LEGAL COMPLIANCE SECTION - ALWAYS PRESENT */}
        <Box sx={{ mb: 4 }}>
          <CombinedLegalCompliance
            onComplianceChange={handleLegalComplianceChange}
            apiRef={{ current: null }} // Mock API ref for preview
            disabled={false}
          />
        </Box>

        {/* CLIENT Signature Section */}
        <Box
          sx={{
            mt: 4,
            p: 4,
            border: 2,
            borderColor: 'divider',
            borderRadius: 1,
            backgroundColor: 'action.hover'
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonOutline /> Client Signature
          </Typography>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Security sx={{ mr: 1 }} />
              <Typography variant="body2">
                <strong>Secure E-Signature:</strong> Your signature will be legally binding and protected 
                with industry-standard security measures.
              </Typography>
            </Box>
          </Alert>
          
          <SignatureCanvas
            onSignatureChange={(signature, type) => setSignatureData(signature)}
            value={signatureData}
            disabled={!legalCompliance.allComplianceComplete}
            required={true}
            error={false}
            helperText={
              legalCompliance.allComplianceComplete ? 
                "Your signature will be securely stored and legally binding" :
                "Please complete the compliance section above before signing"
            }
          />
        </Box>

        {/* ARTIST SECTION: Elements after divider */}
        {artistElements.length > 0 && (
          <Box sx={{ mt: 4 }}>
            {artistElements.map((element, index) => renderFormElement(element, index + clientElements.length))}
          </Box>
        )}

        {/* Submit Button */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button
            variant="contained"
            size="large"
            disabled={!canSubmit}
            sx={{
              px: 6,
              py: 2,
              fontSize: '16px',
              fontWeight: 600,
              backgroundColor: canSubmit ? 'primary.main' : 'action.disabled',
              borderRadius: '8px',
              textTransform: 'none',
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
              '&:hover': {
                backgroundColor: canSubmit ? 'primary.dark' : 'action.disabled',
                boxShadow: canSubmit ? '0 6px 16px rgba(25, 118, 210, 0.4)' : 'none',
              }
            }}
          >
            {canSubmit ? 'Submit Form (Preview Mode)' : 
              !legalCompliance.allComplianceComplete ? 'Complete Legal Requirements' :
              !signatureData ? 'Client Signature Required' :
              'Complete Artist Section'}
          </Button>
        </Box>

        {/* Enhanced Footer with security information */}
        <Box sx={{ mt: 6, pt: 3, borderTop: 1, borderColor: 'divider', textAlign: 'center' }}>
          <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
            This form is secured with electronic signature technology.
          </Typography>
          <Typography sx={{ fontSize: '11px', color: 'text.secondary', mt: 1 }}>
            Legal compliance automatically enforced through integrated CombinedLegalCompliance component.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

// Main Enhanced Form Builder Component remains the same with updated component types
const CustomFormBuilder = ({ initialTemplate, onSave, onCancel }) => {
  // Initialize elements from template
  const initializeElements = () => {
    if (!initialTemplate) {
      return [];
    }

    let elements = [];

    if (initialTemplate.form_config?.elements) {
      elements = initialTemplate.form_config.elements;
    } else if (initialTemplate.elements) {
      elements = initialTemplate.elements;
    } else if (Array.isArray(initialTemplate.form_config)) {
      elements = initialTemplate.form_config;
    }

    return elements;
  };

  const [formElements, setFormElements] = useState(initializeElements());
  const [formMetadata, setFormMetadata] = useState({
    title: initialTemplate?.title || 'My Custom Consent Form',
    description: initialTemplate?.description || '',
    enableIdUpload: initialTemplate?.enable_id_upload || true
  });
  const [activeStep, setActiveStep] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isReordering, setIsReordering] = useState(false);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Use ref to track current form elements for drag handlers (avoids stale closure)
  const formElementsRef = useRef(formElements);
  useEffect(() => {
    formElementsRef.current = formElements;
  }, [formElements]);

  // Track when initialTemplate changes
  useEffect(() => {
    if (initialTemplate) {
      const newElements = initializeElements();
      if (newElements.length > 0 || formElements.length === 0) {
        setFormElements(newElements);
      }

      setFormMetadata({
        title: initialTemplate?.title || 'My Custom Consent Form',
        description: initialTemplate?.description || '',
        enableIdUpload: initialTemplate?.enable_id_upload || true
      });
    }
  }, [initialTemplate?.id]);

  const steps = [
    { label: 'Form Setup', icon: <Settings /> },
    { label: 'Build Your Form', icon: <Assignment /> },
    { label: 'Preview & Save', icon: <CheckCircle /> }
  ];

  const getDefaultProps = (type) => {
    switch (type) {
      case COMPONENT_TYPES.STATIC_TEXT:
        return { content: 'Add your instructions or legal text here...' };
      case COMPONENT_TYPES.SECTION_HEADER:
        return { content: 'Section Title' };
      case COMPONENT_TYPES.TEXT_INPUT:
        return {
          label: 'Text Input',
          name: `text_${Date.now()}`,
          required: false,
          placeholder: 'Enter text here...'
        };
      case COMPONENT_TYPES.CHECKBOX:
        return {
          label: 'I agree to the terms',
          name: `checkbox_${Date.now()}`,
          required: false
        };
      case COMPONENT_TYPES.YES_NO:
        return {
          question: 'Do you have any allergies?',
          name: `yes_no_${Date.now()}`,
          required: false
        };
      case COMPONENT_TYPES.RADIO_GROUP:
        return {
          question: 'What is your preferred appointment time?',
          name: `radio_${Date.now()}`,
          options: ['Morning', 'Afternoon', 'Evening'],
          required: false
        };
      case COMPONENT_TYPES.AFTERCARE_INSTRUCTIONS:
        return {
          instructions: 'Thank you for choosing our studio! Please follow these important aftercare instructions:\n\nâ€¢ Keep the area clean and dry\nâ€¢ Apply recommended ointment as directed\nâ€¢ Avoid direct sunlight and swimming\nâ€¢ Contact us if you have any concerns\n\nWe appreciate your business and want to ensure the best healing process for your new tattoo!',
          enabled: true
        };
      // NEW: Artist component defaults
      case COMPONENT_TYPES.ARTIST_SECTION_DIVIDER:
        return { 
          title: 'Artist Section', 
          instructions: 'The following section is to be completed by the performing artist.' 
        };
      case COMPONENT_TYPES.ARTIST_NUMBER:
        return { 
          label: 'Artist/License Number', 
          placeholder: 'Enter your certification/license number',
          required: true 
        };
      case COMPONENT_TYPES.ARTIST_REVIEW_CHECKBOX:
        return { 
          label: 'I have reviewed this form and confirm all client information is accurate and complete',
          required: true 
        };
      case COMPONENT_TYPES.ARTIST_SIGNATURE:
        return { required: true };
      default:
        return {};
    }
  };

  const addComponent = useCallback((componentType, atIndex = null) => {
    // Prevent adding multiple artist section dividers
    if (componentType === COMPONENT_TYPES.ARTIST_SECTION_DIVIDER) {
      const existingDividerIndex = formElementsRef.current.findIndex(el => el.type === COMPONENT_TYPES.ARTIST_SECTION_DIVIDER);
      if (existingDividerIndex !== -1) {
        alert('An Artist Section Divider already exists. You can only have one divider per form.');
        return;
      }
    }

    const newElement = {
      id: Date.now() + Math.random(),
      type: componentType,
      ...getDefaultProps(componentType)
    };

    if (atIndex !== null && atIndex >= 0) {
      // Insert at specific position
      const newElements = [...formElementsRef.current];
      newElements.splice(atIndex, 0, newElement);
      setFormElements(newElements);
    } else {
      // Add to end (default behavior)
      setFormElements(prev => [...prev, newElement]);
    }
  }, []);

  // Drag handlers for the form builder area
  const handleDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    setIsReordering(false);
    setDragOverIndex(null);
  }, []);

  const handleReorderStart = useCallback(() => {
    setIsReordering(true);
  }, []);

  const handleReorderEnd = useCallback(() => {
    setIsReordering(false);
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback((e, dropIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Try to get data from dataTransfer
    const componentType = e.dataTransfer.getData('componentType');
    const dragType = e.dataTransfer.getData('dragType');
    const elementIndexStr = e.dataTransfer.getData('elementIndex') || e.dataTransfer.getData('text/plain');
    const fromIndex = parseInt(elementIndexStr, 10);
    if (dragType === 'reorder' && !isNaN(fromIndex)) {
      // Reordering existing element
      let targetIndex = dropIndex;
      
      // If dropping after the original position, adjust for the removal
      if (fromIndex < dropIndex) {
        targetIndex = dropIndex - 1;
      }
      
      // Only move if actually changing position
      if (fromIndex !== targetIndex) {
        const currentElements = formElementsRef.current;
        const newElements = [...currentElements];
        const [movedElement] = newElements.splice(fromIndex, 1);
        newElements.splice(targetIndex, 0, movedElement);
        setFormElements(newElements);
      }
    } else if (componentType) {
      // Adding new component from sidebar
      addComponent(componentType, dropIndex);
    } else if (!isNaN(fromIndex)) {
      // Fallback: treat as reorder if we have a valid index
      let targetIndex = dropIndex;
      if (fromIndex < dropIndex) {
        targetIndex = dropIndex - 1;
      }
      if (fromIndex !== targetIndex) {
        const currentElements = formElementsRef.current;
        const newElements = [...currentElements];
        const [movedElement] = newElements.splice(fromIndex, 1);
        newElements.splice(targetIndex, 0, movedElement);
        setFormElements(newElements);
      }
    }
    
    setIsDragging(false);
    setIsReordering(false);
    setDragOverIndex(null);
  }, [addComponent]);

  const updateElement = (index, updatedElement) => {
    const newElements = [...formElements];
    newElements[index] = { ...newElements[index], ...updatedElement };
    setFormElements(newElements);
  };

  const deleteElement = (index) => {
    const newElements = formElements.filter((_, i) => i !== index);
    setFormElements(newElements);
  };

  const moveElement = (fromIndex, toIndex) => {
    const newElements = [...formElements];
    const [movedElement] = newElements.splice(fromIndex, 1);
    newElements.splice(toIndex, 0, movedElement);
    setFormElements(newElements);
  };

  const handleSave = () => {
    // Legal compliance is automatically handled by CombinedLegalCompliance component
    // No need to check for or add legal components manually
    
    const formConfig = {
      title: formMetadata.title,
      description: formMetadata.description,
      enable_id_upload: formMetadata.enableIdUpload,
      form_config: {
        elements: formElements,
        version: '2.2', // Updated version for artist section support
        createdAt: new Date().toISOString(),
        automaticLegalCompliance: true, // Flag indicating automatic legal compliance
        esignCompliant: true, // Always true since CombinedLegalCompliance is automatic
        hasArtistSection: formElements.some(el => el.type === COMPONENT_TYPES.ARTIST_SECTION_DIVIDER)
      },
      metadata: {
        title: formMetadata.title,
        description: formMetadata.description,
        enableIdUpload: formMetadata.enableIdUpload
      },
      elements: formElements
    };

    if (onSave) {
      onSave(formConfig);
    }
  };

  const renderFormSetup = () => (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          Form Setup
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Let's start with basic information about your consent form
        </Typography>

        <Stack spacing={3}>
          <TextField
            fullWidth
            label="Form Title"
            value={formMetadata.title}
            onChange={(e) => setFormMetadata({ ...formMetadata, title: e.target.value })}
            placeholder="e.g., Custom Tattoo Consent Form"
          />

          <TextField
            fullWidth
            label="Form Description"
            multiline
            rows={3}
            value={formMetadata.description}
            onChange={(e) => setFormMetadata({ ...formMetadata, description: e.target.value })}
            placeholder="Brief description of what this form is for..."
          />

          <Box>
            <Typography variant="h6" gutterBottom>
              Additional Features
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={formMetadata.enableIdUpload}
                  onChange={(e) => setFormMetadata({ ...formMetadata, enableIdUpload: e.target.checked })}
                />
              }
              label="Enable ID photo upload"
            />
          </Box>

          <Alert severity="info">
            <Typography variant="body2">
              <strong>Note:</strong> This form builder provides tools to create custom consent forms.
              Terms & Conditions, E-Signature Consent are automatically included at the end of the client section.
              Use the <strong>Artist Section</strong> components to add an area for artist verification and signature.
            </Typography>
          </Alert>
        </Stack>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
          <Button
            variant="contained"
            onClick={() => setActiveStep(1)}
            endIcon={<NavigateNext />}
            size="large"
          >
            Continue to Builder
          </Button>
        </Box>
      </Paper>
    </Container>
  );

  const renderFormBuilder = () => {
    const anyDragging = isDragging || isReordering;
    
    // DropZone component for drag-and-drop positioning
    // Simplified version without local state to prevent re-render issues
    const DropZone = ({ index }) => {
      const isActive = dragOverIndex === index;
      
      return (
        <Box
          data-dropzone={index}
          onDragEnter={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDragOverIndex(index);
            // Ensure we track that dragging is happening
            if (!isReordering) {
              setIsReordering(true);
            }
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = 'move';
            if (dragOverIndex !== index) {
              setDragOverIndex(index);
            }
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // Only clear if we're leaving to outside
            const rect = e.currentTarget.getBoundingClientRect();
            const x = e.clientX;
            const y = e.clientY;
            if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
              if (dragOverIndex === index) {
                setDragOverIndex(null);
              }
            }
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleDrop(e, index);
          }}
          sx={{
            // Always have enough height to be a drop target
            minHeight: '24px',
            height: isActive ? '60px' : anyDragging ? '40px' : '24px',
            transition: 'all 0.15s ease',
            borderRadius: 1,
            border: '2px dashed',
            borderColor: isActive ? 'primary.main' : anyDragging ? 'grey.400' : 'transparent',
            backgroundColor: isActive 
              ? 'rgba(25, 118, 210, 0.2)' 
              : anyDragging 
                ? 'rgba(25, 118, 210, 0.08)' 
                : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            my: 0.5,
            mx: 1,
            cursor: 'default',
            '&:hover': {
              borderColor: anyDragging ? 'primary.light' : 'grey.300',
              backgroundColor: anyDragging ? 'action.selected' : 'action.hover'
            }
          }}
        >
          {anyDragging && (
            <Typography 
              variant="caption" 
              color={isActive ? 'primary' : 'text.secondary'}
              sx={{ 
                fontWeight: isActive ? 600 : 400,
                pointerEvents: 'none' // Prevent text from interfering with drop
              }}
            >
              {isActive ? 'â†“ Drop here â†“' : 'Drop zone'}
            </Typography>
          )}
        </Box>
      );
    };

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <ComponentLibrary 
            onAddComponent={addComponent}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          />
        </Grid>
        <Grid item xs={12} md={9}>
          <Paper 
            elevation={2} 
            sx={{ p: 3, minHeight: '600px' }}
            onDragOver={(e) => {
              // Allow drops anywhere in the paper
              e.preventDefault();
            }}
            onDragEnter={(e) => {
              // Track that we're in a drag operation
              e.preventDefault();
              if (!isReordering && !isDragging) {
                setIsReordering(true);
              }
            }}
            onDragLeave={(e) => {
              // Only clear if we're leaving the paper entirely
              if (!e.currentTarget.contains(e.relatedTarget)) {
                setDragOverIndex(null);
              }
            }}
            onDragEnd={() => {
              // Clean up drag state
              setIsDragging(false);
              setIsReordering(false);
              setDragOverIndex(null);
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" fontWeight={600}>
                Build Your Form
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  label={`${formElements.length} elements`}
                  color="primary"
                  variant="outlined"
                />
              </Box>
            </Box>

            {formElements.length === 0 ? (
              <Box
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOverIndex(0);
                }}
                onDrop={(e) => handleDrop(e, 0)}
                sx={{
                  textAlign: 'center',
                  py: 8,
                  border: dragOverIndex === 0 ? '2px solid' : '2px dashed',
                  borderColor: dragOverIndex === 0 ? 'primary.main' : 'divider',
                  borderRadius: 2,
                  bgcolor: dragOverIndex === 0 ? 'rgba(25, 118, 210, 0.1)' : 'action.hover',
                  transition: 'all 0.2s ease'
                }}
              >
                <Assignment sx={{ fontSize: 48, color: dragOverIndex === 0 ? 'primary.main' : 'grey.400', mb: 2 }} />
                <Typography variant="h6" color={dragOverIndex === 0 ? 'primary' : 'text.secondary'} gutterBottom>
                  {dragOverIndex === 0 ? 'Drop component here' : 'Start building your form'}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  {dragOverIndex === 0 ? 'Release to add the component' : 'Drag components from the sidebar or click to add'}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ position: 'relative' }}>
                {/* Drop zone at the very top */}
                <DropZone index={0} />
                
                {formElements.map((element, index) => (
                  <React.Fragment key={element.id}>
                    <SimpleFormElement
                      element={element}
                      index={index}
                      onUpdate={updateElement}
                      onDelete={deleteElement}
                      onMove={moveElement}
                      totalElements={formElements.length}
                      onReorderStart={handleReorderStart}
                      onReorderEnd={handleReorderEnd}
                    />
                    {/* Drop zone after each element */}
                    <DropZone index={index + 1} />
                  </React.Fragment>
                ))}

                <Alert severity="success" sx={{ mt: 3 }}>
                  <Typography variant="body2">
                    Great progress! You have {formElements.length} element{formElements.length !== 1 ? 's' : ''} in your form.
                    {anyDragging && ' Drag to a drop zone to reorder.'}
                    {formElements.some(el => el.type === COMPONENT_TYPES.ARTIST_SECTION_DIVIDER) && (
                      <><br /><strong>âœ“ Artist section enabled</strong></>
                    )}
                  </Typography>
                </Alert>
              </Box>
            )}
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => setActiveStep(0)}
              startIcon={<NavigateBefore />}
              size="large"
            >
              Back to Setup
            </Button>
            <Button
              variant="contained"
              onClick={() => setActiveStep(2)}
              endIcon={<NavigateNext />}
              size="large"
            >
              Preview & Save
            </Button>
          </Box>
        </Grid>
      </Grid>
    );
  };

  const renderPreviewAndSave = () => {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight={600} gutterBottom textAlign="center">
            Form Preview
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center">
            This is exactly how your clients will see the form
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Note:</strong> Terms & Conditions and E-Signature Consent 
            are automatically included at the bottom of the client section through the CombinedLegalCompliance component.
            {formElements.some(el => el.type === COMPONENT_TYPES.ARTIST_SECTION_DIVIDER) && (
              <><br /><strong>Artist Section:</strong> This form includes an artist verification section.</>
            )}
          </Typography>
        </Alert>

        <ProfessionalFormRenderer
          formMetadata={formMetadata}
          formElements={formElements}
        />

        <Box sx={{ mt: 4, p: 3, bgcolor: 'success.50', borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} color="success.dark" gutterBottom>
            Form Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">Custom Elements</Typography>
              <Typography variant="h6" fontWeight={600}>{formElements.length}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">Required Fields</Typography>
              <Typography variant="h6" fontWeight={600}>
                {formElements.filter(el => el.required).length}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">Artist Section</Typography>
              <Typography variant="h6" fontWeight={600} color={formElements.some(el => el.type === COMPONENT_TYPES.ARTIST_SECTION_DIVIDER) ? 'success.main' : 'text.secondary'}>
                {formElements.some(el => el.type === COMPONENT_TYPES.ARTIST_SECTION_DIVIDER) ? 'Enabled' : 'None'}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">Form Status</Typography>
              <Typography variant="h6" fontWeight={600} color="success.main">
                Ready
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            variant="outlined"
            onClick={() => setActiveStep(1)}
            startIcon={<NavigateBefore />}
            size="large"
          >
            Back to Builder
          </Button>

          <Box sx={{ display: 'flex', gap: 2 }}>
            {onCancel && (
              <Button
                variant="outlined"
                onClick={onCancel}
                size="large"
              >
                Cancel
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleSave}
              startIcon={<Save />}
              size="large"
              sx={{
                background: 'linear-gradient(45deg, #4caf50 30%, #1976d2 90%)'
              }}
            >
              {initialTemplate ? 'Update Form' : 'Save Form'}
            </Button>
          </Box>
        </Box>
      </Container>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 3 }}>
      <Container maxWidth="xl">
        <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                {initialTemplate ? 'Edit Form Template' : 'Create Custom Form'}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Build professional consent forms with automatic legal compliance integration
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<GetApp />}
                onClick={() => {
                  const formConfig = {
                    metadata: formMetadata,
                    elements: formElements,
                    version: '2.2',
                    createdAt: new Date().toISOString(),
                    automaticLegalCompliance: true,
                    hasArtistSection: formElements.some(el => el.type === COMPONENT_TYPES.ARTIST_SECTION_DIVIDER)
                  };
                  const blob = new Blob([JSON.stringify(formConfig, null, 2)], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${formMetadata.title.replace(/\s+/g, '_')}_form_config.json`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
              >
                Export
              </Button>
            </Box>
          </Box>
        </Paper>

        <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((step, index) => (
              <Step key={step.label} onClick={() => setActiveStep(index)} sx={{ cursor: 'pointer' }}>
                <StepLabel
                  StepIconComponent={({ active, completed }) => (
                    <Avatar
                      sx={{
                        bgcolor: completed ? 'success.main' : active ? 'primary.main' : 'grey.300',
                        color: 'white',
                        width: 40,
                        height: 40
                      }}
                    >
                      {completed ? <CheckCircle /> : step.icon}
                    </Avatar>
                  )}
                >
                  {step.label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        <Fade in key={activeStep}>
          <Box>
            {activeStep === 0 && renderFormSetup()}
            {activeStep === 1 && renderFormBuilder()}
            {activeStep === 2 && renderPreviewAndSave()}
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

// Export the form renderer as a named export for use in other parts of the app
export { ProfessionalFormRenderer };

// Main default export - this is what gets imported when you do: import CustomFormBuilder from './CustomFormBuilder'
export default CustomFormBuilder;